// Webflow Dropdown widget (.w-dropdown). Opens on hover when `data-hover="true"` (desktop,
// non-touch), and on click otherwise; closes on outside click, Escape, or mouse leave after
// `data-delay` ms. Open state is expressed with the same classes webflow.js used.

import { createContext, useContext, useEffect, useId, useRef, useState } from 'react';
import { animateHeight, cx, type WfProps } from './element';
import { InNavOverlay, useNavbar } from './Navbar';

interface DropdownState {
  open: boolean;
  setOpen: (open: boolean) => void;
  inNav: boolean;
  id: string;
}

const DropdownContext = createContext<DropdownState | null>(null);

const isTouch = () => typeof window !== 'undefined' && document.documentElement.classList.contains('w-mod-touch');

export function Dropdown({ as: Tag = 'div', className, style, children, ...props }: WfProps) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const inOverlay = useContext(InNavOverlay);
  const inNav = useNavbar() !== null;
  const hover = props['data-hover'] === 'true' && !inOverlay;
  const delay = Number(props['data-delay'] ?? 0);
  const id = useId().replace(/:/g, '');

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const hoverHandlers = hover
    ? {
        onMouseEnter: () => {
          if (isTouch()) return;
          clearTimeout(closeTimer.current);
          setOpen(true);
        },
        onMouseLeave: () => {
          if (isTouch()) return;
          closeTimer.current = setTimeout(() => setOpen(false), delay);
        },
      }
    : {};

  return (
    <DropdownContext.Provider value={{ open, setOpen, inNav: inNav && inOverlay, id }}>
      <Tag
        ref={root}
        {...props}
        className={cx(className, open && inOverlay && 'w--nav-dropdown-open')}
        style={open ? { ...style, zIndex: 901 } : style}
        {...hoverHandlers}
      >
        {children}
      </Tag>
    </DropdownContext.Provider>
  );
}

export function DropdownToggle({ as: Tag = 'div', className, children, ...props }: WfProps) {
  const dd = useContext(DropdownContext);
  const toggle = () => dd?.setOpen(!dd.open);
  return (
    <Tag
      {...props}
      className={cx(className, dd?.open && 'w--open', dd?.open && dd.inNav && 'w--nav-dropdown-toggle-open')}
      id={dd ? `w-dropdown-toggle-${dd.id}` : undefined}
      aria-controls={dd ? `w-dropdown-list-${dd.id}` : undefined}
      aria-haspopup="menu"
      aria-expanded={dd?.open ?? false}
      role="button"
      tabIndex={0}
      onClick={(e: React.MouseEvent) => {
        e.preventDefault();
        toggle();
      }}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggle();
        }
      }}
    >
      {children}
    </Tag>
  );
}

export function DropdownList({ as: Tag = 'nav', className, children, ...props }: WfProps) {
  const dd = useContext(DropdownContext);
  const list = useRef<HTMLElement>(null);
  const inNavbar = useNavbar() !== null;

  // Navbar dropdowns expand in place inside the collapsed (tablet/mobile) menu.
  useEffect(() => {
    if (!dd?.open || !list.current || !inNavbar) return;
    if (!window.matchMedia('(max-width: 991px)').matches) return;
    list.current.style.height = '0px';
    animateHeight(list.current, true, 300);
  }, [dd?.open, inNavbar]);

  return (
    <Tag
      ref={list}
      {...props}
      className={cx(className, dd?.open && 'w--open', dd?.open && dd.inNav && 'w--nav-dropdown-list-open')}
      id={dd ? `w-dropdown-list-${dd.id}` : undefined}
      aria-labelledby={dd ? `w-dropdown-toggle-${dd.id}` : undefined}
    >
      {children}
    </Tag>
  );
}
